import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { fuseAnimations } from '../../../../@fuse/animations';
import { CrudEmployeeService } from '../../../gt2/content/crud/crud-employee/crud-employee.service';
import { ChatStore } from '../../../services/chat/chat-store.service';

/**
 * Shell 3 volets du chat (Démo A dynamisée T5) — initialise le ChatStore avec
 * l'identité du user courant (shape sender backend : uuid User, nom, avatar).
 */
@Component({
  selector: 'chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class ChatComponent implements OnInit, OnDestroy {
  private _unsubscribeAll = new Subject<void>();

  // DM demandé via ?dm=<userUuid> (rail d'avatars) — ouvert dès que le store
  // est initialisé.
  private _pendingDmUserUuid: string | null = null;
  private _storeReady = false;

  constructor(
    public store: ChatStore,
    private crudEmployeeService: CrudEmployeeService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Le rail d'avatars navigue avec ?dm=<userUuid> ; on écoute (et non un
    // snapshot) car un clic pendant qu'on est déjà sur /chat ne recrée pas le
    // composant.
    this.route.queryParams
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((params) => {
        const dm = params['dm'];
        if (dm) {
          this._pendingDmUserUuid = dm;
          this.openPendingDm();
        }
      });

    const me = this.crudEmployeeService.me;
    if (me?.user?.uuid) {
      this.initStore(me);
      return;
    }

    // me pas encore chargé (accès direct par URL) — attendre le CrudEmployeeService.
    this.crudEmployeeService.getMe().pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (loaded: any) => {
        if (loaded?.user?.uuid) {
          this.initStore(loaded);
        }
      },
    });
  }

  private initStore(me: any): void {
    this.store.init({
      uuid: me.user.uuid,
      name: me.full_name ?? `${me.first_name} ${me.last_name}`,
      avatar_thumb: me.avatar_thumb ?? null,
    });
    this._storeReady = true;
    this.openPendingDm();
  }

  /** Ouvre (ou crée) le DM demandé, puis retire ?dm de l'URL pour éviter une
   *  réouverture au refresh. */
  private openPendingDm(): void {
    if (!this._storeReady || !this._pendingDmUserUuid) {
      return;
    }
    const userUuid = this._pendingDmUserUuid;
    this._pendingDmUserUuid = null;
    this.store.createDirect(userUuid);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { dm: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.store.closeConversation();
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
